import { useState } from "react";
import { Check, X, Trophy, Sparkles } from "lucide-react";
import { apiFetch } from "@/api";
import { Button, Spinner, cn } from "@/ui";
import { toast } from "sonner";

function cleanExplanation(text) {
  const t = (text || "").trim();
  if (!t) return t;
  const stripped = t.replace(/^\s*(correct|incorrect|right|wrong|yes|no|exactly|nope|indeed|not quite|that'?s (right|correct|wrong|incorrect))[\s!.,:;—-]+/i, "").trim();
  const out = stripped || t;
  return out.charAt(0).toUpperCase() + out.slice(1);
}

export function QuizPlayer({ session, onClose, onDone }) {
  const qs = session.questions || [];
  const answered = session.my_answers || {};
  const firstUnanswered = qs.findIndex((q) => !(q.index in answered));
  const [idx, setIdx] = useState(firstUnanswered === -1 ? 0 : firstUnanswered);
  const [picked, setPicked] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const q = qs[idx];
  const isLast = idx >= qs.length - 1;

  const pick = async (choiceIndex) => {
    if (busy || result) return;
    setPicked(choiceIndex);
    setBusy(true);
    try {
      const res = await apiFetch(`/quiz/${session.session_id}/answer`, {
        method: "POST", body: { q_index: q.index, choice_index: choiceIndex },
      });
      setResult(res);
    } catch (e) {
      toast(e.message || "Couldn't submit that answer.");
      setPicked(null);
    } finally {
      setBusy(false);
    }
  };

  const next = async () => {
    if (!isLast) {
      setIdx((i) => i + 1);
      setPicked(null);
      setResult(null);
      return;
    }
    setBusy(true);
    try {
      const fin = await apiFetch(`/quiz/${session.session_id}/finish`, { method: "POST" });
      setDone(fin);
    } catch (e) {
      toast(e.message || "Couldn't finish the quiz.");
    } finally {
      setBusy(false);
    }
  };

  const finishAndClose = () => { onDone?.(done); onClose?.(); };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center">
      <div data-testid="quiz-player" className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-t-3xl border border-border bg-background sm:rounded-3xl">
        {done ? (
          <div className="flex flex-col items-center gap-5 px-6 py-10 text-center">
            <span className="flex h-20 w-20 items-center justify-center rounded-3xl bg-learn/15"><Trophy className="h-10 w-10 text-learn" /></span>
            <h2 className="font-display text-2xl font-bold">Quiz complete!</h2>
            <div data-testid="quiz-scoreboard" className="w-full rounded-2xl border border-border">
              {(done.scoreboard || []).map((r, i) => (
                <div key={r.account_id} className={cn("flex items-center justify-between px-4 py-3", i > 0 && "border-t border-border")}>
                  <span className="flex items-center gap-2 font-semibold">{i === 0 ? "🏆" : `${i + 1}.`} {r.name}</span>
                  <span className="text-sm text-muted-foreground">{r.correct}/{r.total} correct</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 text-sm">
              <span data-testid="quiz-total-xp" className="font-bold">{done.total_xp} XP</span>
              {done.streak_days ? <span className="text-muted-foreground">🔥 {done.streak_days}-day streak</span> : null}
            </div>
            <Button data-testid="quiz-done-btn" className="w-full" onClick={finishAndClose}>Done</Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <div className="min-w-0">
                <div className="text-xs font-bold uppercase tracking-wider text-learn">{session.topic || "Quiz"}</div>
                <div className="text-sm text-muted-foreground">Question {idx + 1} of {qs.length}</div>
              </div>
              <button data-testid="quiz-close-btn" onClick={() => (done ? finishAndClose() : onClose?.())} className="rounded-full p-1.5 text-muted-foreground hover:bg-accent"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-auto px-5 py-6">
              <h3 className="font-display text-xl font-bold leading-snug">{q?.question}</h3>
              <div className="mt-5 flex flex-col gap-2.5">
                {(q?.choices || []).map((c, i) => {
                  const isCorrect = result && i === result.correct_index;
                  const isWrongPick = result && i === picked && !result.correct;
                  return (
                    <button
                      key={i}
                      data-testid={`quiz-choice-${i}`}
                      disabled={!!result || busy}
                      onClick={() => pick(i)}
                      className={cn(
                        "flex items-center justify-between gap-3 rounded-2xl border px-4 py-3.5 text-left text-base transition-colors",
                        !result && "border-border hover:border-primary/50 hover:bg-accent/40",
                        isCorrect && "border-green-500 bg-green-500/10",
                        isWrongPick && "border-red-500 bg-red-500/10",
                        result && !isCorrect && !isWrongPick && "border-border opacity-60",
                      )}
                    >
                      <span>{c}</span>
                      {isCorrect ? <Check className="h-5 w-5 shrink-0 text-green-500" /> : null}
                      {isWrongPick ? <X className="h-5 w-5 shrink-0 text-red-500" /> : null}
                    </button>
                  );
                })}
              </div>
              {result ? (
                <div className="mt-5 rounded-2xl bg-secondary px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className={cn("font-bold", result.correct ? "text-green-500" : "text-red-500")}>
                      {result.correct ? "Correct!" : "Not quite"}
                    </span>
                    {result.awarded > 0 ? (
                      <span data-testid="quiz-xp-pop" className="flex animate-in zoom-in items-center gap-1 rounded-full bg-learn/15 px-2.5 py-1 text-sm font-bold text-learn">
                        <Sparkles className="h-3.5 w-3.5" /> +{result.awarded} XP
                      </span>
                    ) : null}
                  </div>
                  {result.explanation ? <p className="mt-1.5 text-sm text-muted-foreground">{cleanExplanation(result.explanation)}</p> : null}
                </div>
              ) : null}
            </div>
            <div className="border-t border-border px-5 py-4">
              {result ? (
                <Button data-testid="quiz-next-btn" className="w-full" loading={busy} onClick={next}>
                  {isLast ? "Finish quiz" : "Next question"}
                </Button>
              ) : (
                <div className="flex items-center justify-center py-1 text-sm text-muted-foreground">
                  {busy ? <Spinner className="h-4 w-4" /> : "Pick an answer"}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
